import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentDisclosuresBrokerageData,
  type EnrollmentDisclosuresBrokerageSubmitInput,
  type EnrollmentDisclosuresBrokerageSubmitOutput,
} from '../../../models'
import { createMutationHook, createQueryHook } from '../../../hooks'

/**
 * Get Enrollment DisclosuresBrokerage data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentDisclosuresBrokerageData>
 * @example const response = await getEnrollmentDisclosuresBrokerageData()
 */
export const getEnrollmentDisclosuresBrokerageData = async (
  context?: DefaultContext,
): Promise<EnrollmentDisclosuresBrokerageData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentDisclosuresBrokerageData',
      query: `mutation getEnrollmentDisclosuresBrokerageData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
                experimentId
                applicationId
                userId
                eSessionId
                userInfo {
                    employmentStatus
                    occupation
                    netWorthRange
                    income
                    investment {
                        riskTolerance {
                            type
                            allocation {
                                equityPercent
                                fixedIncomePercent
                                cashEquivalentPercent
                            }
                        }
                    }
                }
                investmentProfile {
                    objective
                    experience
                    riskTolerance
                    fpslParticipation
                    taxFilingStatus
                    disclosure {
                        customerAccountAgreement {
                            accepted
                        }
                        termsOfUse {
                            accepted
                        }
                        privacyPolicy {
                            accepted
                        }
                        rule14b {
                            accepted
                        }
                        fpslDisclosure {
                            accepted
                        }
                        fpslMasterSecuritiesLendingAgreement {
                            accepted
                        }
                        marginAgreement {
                            accepted
                        }
                    }
                    account {
                        brokerage {
                            accountNickname
                        }
                        accountType
                        includeCompanionAccount
                    }
                }
                recommendation {
                    investment {
                        riskTolerance {
                            recommendedRiskTolerance
                            allowedRiskTolerances
                            riskTolerances {
                                category
                                index
                                allocation {
                                    equityPercent
                                    fixedIncomePercent
                                    cashEquivalentPercent
                                }
                            }
                        }
                        equitySector {
                            type
                            maxAllocationPercent
                            recommendedRange {
                                recommendedPercent
                                allowedMinPercent
                                allowedMaxPercent
                            }
                        }
                        fixedIncomeCategory {
                            type
                            maxAllocationPercent
                            recommendedRange {
                                recommendedPercent
                                allowedMinPercent
                                allowedMaxPercent
                            }
                        }
                        cashEquivalentType {
                            recommended
                            allowTripleTaxFreeMunicipalMutualFund
                        }
                        account {
                            brokerage {
                                accountNickname
                            }
                        }
                    }
                }
                achAuthorization {
                    id
                    version
                    accepted
                    recordedTimestamp
                }
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentDisclosuresBrokerageData }
  return response.submitEnrollment
}

export const useGetEnrollmentDisclosuresBrokerageData = createQueryHook<undefined, EnrollmentDisclosuresBrokerageData>(
  getEnrollmentDisclosuresBrokerageData,
)

/**
 * Submit Enrollment DisclosuresBrokerage data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentDisclosuresBrokerageSubmitOutput>
 * @example const response = await submitEnrollmentDisclosuresBrokerageData();
 */
export const submitEnrollmentDisclosuresBrokerageData = async (
  enrollmentDisclosuresBrokerageSubmitInput: EnrollmentDisclosuresBrokerageSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentDisclosuresBrokerageSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentDisclosuresBrokerageData',
      query: `mutation submitEnrollmentDisclosuresBrokerageData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentDisclosuresBrokerageSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentDisclosuresBrokerageSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentDisclosuresBrokerageData = createMutationHook<
  EnrollmentDisclosuresBrokerageSubmitInput,
  EnrollmentDisclosuresBrokerageSubmitOutput
>(submitEnrollmentDisclosuresBrokerageData)
